import React, { useState } from "react";
import { Box, InputAdornment, Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import i18n from "i18next";
import { faExternalLinkAlt, faLock, faPen } from "@fortawesome/pro-regular-svg-icons";
import {
	PartConfigurationDocumentValue,
	PartConfigurationPropertyEvaluation,
	PartConfigurationSchemaOption,
	PartConfigurationSchemaProperty,
	PartConfigurationValue,
} from "src/components/views/erp/configurator/configuratorForm/propertyModel/partConfigurationSdk.ts";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { AavoSelectField } from "src/components/common/inputFields/AavoSelectField.tsx";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField.tsx";
import { AavoNumberField } from "src/components/common/inputFields/AavoNumberField.tsx";
import { AavoCheckbox } from "src/components/common/inputFields/AavoCheckbox.tsx";
import { AavoFileInput } from "src/components/common/inputFields/fileInput/AavoFileInput.tsx";
import { useInputDialog } from "src/components/common/dialogs/input/useInputDialog.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { uploadFile } from "src/utils/fileUploading.ts";
import { UnmanagedFilesApi } from "src/api/generated/documents/unmanagedFiles/unmanagedFilesApi.ts";
import { FilePreviewView } from "src/components/views/documents/preview/FilePreviewView.tsx";
import { getFileExtension } from "src/utils/fileUtils.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import {
	getPropertyModelDocumentValue,
	getPropertyModelFieldHelperText,
	getPropertyModelSelectableOptions,
} from "src/components/views/erp/configurator/configuratorForm/propertyModel/propertyModelInputFieldUtils.ts";

export interface PropertyModelInputFieldProps {
	property: PartConfigurationSchemaProperty;
	evaluation: PartConfigurationPropertyEvaluation;
	issueMessages: string[];
	disabled: boolean;
	/** Sets the input; undefined removes it so that it gets its default. */
	onChange: (value: PartConfigurationValue | undefined) => Promise<unknown>;
}

export function PropertyModelInputField(props: PropertyModelInputFieldProps) {
	const { evaluation, disabled } = props;
	return <PropertyModelInputFieldControl {...props} disabled={disabled || evaluation.fixed} />;
}

function PropertyModelInputFieldControl({
	property,
	evaluation,
	issueMessages,
	disabled,
	onChange,
}: PropertyModelInputFieldProps) {
	const label = `${property.title || property.name}${evaluation.required ? " *" : ""}`;
	const error = issueMessages.length > 0 ? issueMessages.join("; ") : undefined;
	const helperText = getPropertyModelFieldHelperText(evaluation);
	const fixedIcon = evaluation.fixed ? <PropertyModelFixedValueIcon /> : undefined;
	const value = evaluation.value;

	if (property.valuesFromOptions) {
		return (
			<PropertyModelOptionField
				property={property}
				evaluation={evaluation}
				label={label}
				error={error}
				helperText={helperText}
				fixedIcon={fixedIcon}
				disabled={disabled}
				onChange={onChange}
			/>
		);
	}

	switch (property.datatype) {
		case "BOOL":
			return (
				<AavoCheckbox
					label={
						<HorizontalBox sx={{ alignItems: "center", gap: 1 }}>
							{label}
							{fixedIcon}
						</HorizontalBox>
					}
					checked={value === true}
					disabled={disabled}
					error={error}
					helperText={helperText}
					onChange={(checked) => onChange(checked)}
				/>
			);
		case "INTEGER":
		case "DECIMAL":
			return (
				<PropertyModelNumberField
					// Starts editing from the evaluated value again when it changes.
					key={String(value)}
					label={label}
					type={property.datatype === "INTEGER" ? "integer" : "decimal"}
					initialValue={typeof value === "number" ? value : null}
					disabled={disabled}
					error={error}
					helperText={helperText}
					endAdornment={fixedIcon}
					onChange={onChange}
				/>
			);
		case "DOCUMENT":
			return (
				<PropertyModelDocumentField
					// Shows the evaluated file again when the value changes.
					key={getPropertyModelDocumentValue(value)?.value ?? ""}
					property={property}
					value={getPropertyModelDocumentValue(value)}
					label={label}
					error={error}
					disabled={disabled}
					onChange={onChange}
				/>
			);
		default:
			return (
				<PropertyModelTextField
					// Starts editing from the evaluated value again when it changes.
					key={String(value)}
					label={label}
					initialValue={typeof value === "string" ? value : ""}
					disabled={disabled}
					error={error}
					helperText={helperText}
					endAdornment={fixedIcon}
					onChange={onChange}
				/>
			);
	}
}

interface PropertyModelEditableFieldProps<T> {
	label: string;
	initialValue: T;
	disabled: boolean;
	error: string | undefined;
	helperText: string | undefined;
	endAdornment: React.ReactNode;
	onChange: (value: PartConfigurationValue | undefined) => Promise<unknown>;
}

function PropertyModelTextField({
	label,
	initialValue,
	disabled,
	error,
	helperText,
	endAdornment,
	onChange,
}: PropertyModelEditableFieldProps<string>) {
	const [draft, setDraft] = useState(initialValue);
	return (
		<AavoTextField
			label={label}
			value={draft}
			disabled={disabled}
			error={error}
			helperText={helperText}
			slotProps={{ input: { endAdornment: toInputAdornment(endAdornment) } }}
			sx={{ flex: 1 }}
			onChange={setDraft}
			onSubmit={(newValue) => onChange(newValue === "" ? undefined : newValue)}
		/>
	);
}

interface PropertyModelNumberFieldProps extends PropertyModelEditableFieldProps<number | null> {
	type: "integer" | "decimal";
}

function PropertyModelNumberField({
	label,
	type,
	initialValue,
	disabled,
	error,
	helperText,
	endAdornment,
	onChange,
}: PropertyModelNumberFieldProps) {
	const [draft, setDraft] = useState(initialValue);
	return (
		<AavoNumberField
			label={label}
			type={type}
			value={draft}
			disabled={disabled}
			error={error}
			helperText={helperText}
			slotProps={{ input: { endAdornment: toInputAdornment(endAdornment) } }}
			sx={{ flex: 1 }}
			onChange={setDraft}
			onSubmit={(newValue) => onChange(newValue == null || isNaN(newValue) ? undefined : newValue)}
		/>
	);
}

interface PropertyModelOptionFieldProps {
	property: PartConfigurationSchemaProperty;
	evaluation: PartConfigurationPropertyEvaluation;
	label: string;
	error: string | undefined;
	helperText: string | undefined;
	fixedIcon: React.ReactNode;
	disabled: boolean;
	onChange: (value: PartConfigurationValue | undefined) => Promise<unknown>;
}

function PropertyModelOptionField({
	property,
	evaluation,
	label,
	error,
	helperText,
	fixedIcon,
	disabled,
	onChange,
}: PropertyModelOptionFieldProps) {
	const showInputDialog = useInputDialog();
	const value = typeof evaluation.value === "string" ? evaluation.value : null;
	const options = getPropertyModelSelectableOptions({ property, evaluation });
	// A value fixed by a rule cannot be replaced with a free value either.
	const canEnterFreeValue = property.allowsFreeValue && !evaluation.fixed;

	return (
		<AavoSelectField<PartConfigurationSchemaOption, string, false>
			label={label}
			value={value}
			options={options}
			getOptionKey={(option) => option.key}
			getOptionLabel={(option) => option.label || option.key}
			disabled={disabled}
			error={error}
			helperText={helperText}
			endAdornment={
				fixedIcon != null || canEnterFreeValue ?
					<>
						{fixedIcon}
						{canEnterFreeValue && (
							<AsyncButton
								icon={faPen}
								size={"small"}
								tooltip={i18n.t("enter_free_value")}
								disabled={disabled}
								onClick={enterFreeValue}
							/>
						)}
					</>
				:	undefined
			}
			// Long values are cut instead of widening the field beyond its grid column.
			sx={{ flex: 1, minWidth: 0 }}
			onChange={(key) => onChange(key ?? undefined)}
		/>
	);

	async function enterFreeValue() {
		const freeValue = await showInputDialog({
			type: "string",
			title: i18n.t("enter_free_value"),
			defaultValue: value ?? "",
		});
		if (freeValue === undefined) return;
		await onChange(freeValue === "" ? undefined : freeValue);
	}
}

/** Marks a value that a rule fixes, so the user cannot change it. */
function PropertyModelFixedValueIcon() {
	return (
		<Tooltip title={i18n.t("value_fixed_by_rule")}>
			<Box component={"span"} sx={{ display: "flex", color: "text.secondary", paddingX: 0.5 }}>
				<FontAwesomeIcon icon={faLock} aria-label={i18n.t("value_fixed_by_rule")} />
			</Box>
		</Tooltip>
	);
}

function toInputAdornment(content: React.ReactNode) {
	return content == null ? undefined : <InputAdornment position={"end"}>{content}</InputAdornment>;
}

interface PropertyModelDocumentFieldProps {
	property: PartConfigurationSchemaProperty;
	value: PartConfigurationDocumentValue | undefined;
	label: string;
	error: string | undefined;
	disabled: boolean;
	onChange: (value: PartConfigurationValue | undefined) => Promise<unknown>;
}

function PropertyModelDocumentField({
	property,
	value,
	label,
	error,
	disabled,
	onChange,
}: PropertyModelDocumentFieldProps) {
	const { openDialog } = useGenericDialog();
	const [file, setFile] = useState<File | null>(value == null ? null : new File([], value.fileName));
	const [valueHasChanged, setValueHasChanged] = useState(false);
	const documentCategoryId = property.documentCategoryId;

	return (
		<HorizontalBox sx={{ flex: 1, gap: 1 }}>
			<AavoFileInput<false>
				multiple={false}
				value={file}
				label={label}
				disabled={disabled || documentCategoryId == null}
				error={error ?? (documentCategoryId == null ? i18n.t("document_property_has_no_category") : undefined)}
				placeholder={i18n.t("select_a_file")}
				hideSizeText={!valueHasChanged}
				onChange={onFileChanged}
			/>
			{value != null && <AsyncButton icon={faExternalLinkAlt} tooltip={i18n.t("open")} onClick={openFile} />}
		</HorizontalBox>
	);

	async function onFileChanged(newFile: File | null) {
		setFile(newFile);
		setValueHasChanged(true);
		if (newFile == null) {
			await onChange(undefined);
			return;
		}
		if (documentCategoryId == null) return;
		const fileHandle = await uploadFile(newFile);
		const fileUuid = await UnmanagedFilesApi.createUnmanagedFile({ fileHandle });
		const documentValue: PartConfigurationDocumentValue = {
			value: fileUuid,
			documentDescription: property.title || property.name,
			documentCategoryId: documentCategoryId,
			fileName: newFile.name,
		};
		await onChange(documentValue);
	}

	async function openFile() {
		if (value == null) return;
		const fileUrl = await UnmanagedFilesApi.getUnmanagedFilePresignedDownloadUrl({ uuid: value.value });
		openDialog({
			size: "fullscreen",
			title: value.fileName,
			content: <FilePreviewView fileUrl={fileUrl} extension={getFileExtension(value.fileName)!} />,
			agentContext: {
				key: defineAgentUiContextKey("erp.configurator.propertyModelDocumentPreview.dialog"),
				title: value.fileName,
				data: {
					fileUuid: value.value,
					fileName: value.fileName,
					propertyName: property.name,
				},
			},
		});
	}
}
